import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Shield, CheckCircle, ArrowRight, Building, Heart, Landmark, ShoppingCart, Factory, Code } from "lucide-react";
import { useConsultationEnabled } from "@/hooks/useConsultationEnabled";

const FractionalCISOService = () => {
  const consultationEnabled = useConsultationEnabled();
  const enhancedSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "4FOX Security - Fractional CISO Services UK",
    "alternateName": ["Virtual CISO UK", "vCISO Services", "Part-Time CISO UK"],
    "image": "https://4fox.uk/nerd-fox.png",
    "@id": "https://4fox.uk/services/fractional-ciso-uk",
    "url": "https://4fox.uk/services/fractional-ciso-uk",
    "telephone": "+44 1934 808 525",
    "priceRange": "££",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "T & L Business Park, Winterstoke Road",
      "addressLocality": "Weston-super-Mare",
      "addressRegion": "Somerset",
      "postalCode": "BS23 3YS",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 51.3362752,
      "longitude": -2.9548917
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "United Kingdom"
      },
      {
        "@type": "State",
        "name": "Somerset"
      },
      {
        "@type": "State",
        "name": "North Somerset"
      },
      {
        "@type": "City",
        "name": "Weston-super-Mare"
      },
      {
        "@type": "City",
        "name": "Bristol"
      },
      {
        "@type": "State",
        "name": "South Gloucestershire"
      }
    ],
    "serviceType": ["Fractional CISO", "Virtual CISO", "vCISO", "Part-time CISO", "Interim CISO"],
    "description": "Expert fractional CISO services for UK businesses. Strategic cyber security leadership on a part-time basis without full-time costs. GDPR expertise, UK compliance, and 24/7 support.",
    "provider": {
      "@type": "Organisation",
      "name": "4FOX Security",
      "url": "https://4fox.uk"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How much does a fractional CISO cost in the UK?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fractional CISO services typically cost £3,000-£8,000 per month depending on your organisation's size and complexity. This is significantly less than a full-time CISO salary (£90,000-£150,000+) plus benefits, offering enterprise-level expertise at a fraction of the cost."
        }
      },
      {
        "@type": "Question",
        "name": "What's the difference between a fractional CISO and a vCISO?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fractional CISO and vCISO (virtual CISO) are essentially the same service with different names. Both provide part-time, strategic cyber security leadership without the full-time commitment. Some organisations also use the term 'interim CISO' or 'part-time CISO' to describe this service."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need a fractional CISO or a full-time CISO?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Organisations with 50-500 employees typically benefit most from a fractional CISO. If you need strategic security leadership but can't justify or afford a full-time executive, a fractional CISO provides the expertise you need at a fraction of the cost. Companies with 500+ employees or highly regulated environments may require a full-time CISO."
        }
      },
      {
        "@type": "Question",
        "name": "How many hours per month does a fractional CISO work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Typical fractional CISO engagements include 20-40 hours per month of strategic guidance and hands-on support. This covers quarterly board reports, bi-weekly leadership meetings, security roadmap development, and on-demand availability for urgent security matters."
        }
      },
      {
        "@type": "Question",
        "name": "Can a fractional CISO help with Cyber Essentials certification?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our fractional CISO services include guidance for achieving Cyber Essentials and Cyber Essentials Plus certification. We help you implement the required controls, prepare documentation, and navigate the certification process. Recent clients have achieved certification within 3 months of engagement."
        }
      },
      {
        "@type": "Question",
        "name": "What industries benefit most from fractional CISO services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fractional CISO services benefit organisations across all industries, particularly financial services, healthcare, professional services (legal/accounting), technology companies, manufacturing, and retail. Any UK business handling sensitive data or facing regulatory requirements can benefit from strategic security leadership."
        }
      },
      {
        "@type": "Question",
        "name": "Is a fractional CISO available for incident response?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, our fractional CISO services include on-demand availability for urgent security matters and incident response support. We provide incident response protocols, crisis management guidance, and are available 24/7 for critical security events."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly can a fractional CISO start?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We can typically begin fractional CISO engagements within 1-2 weeks of initial consultation. The first 30 days focus on comprehensive security posture assessment, stakeholder interviews, and quick-win identification to deliver immediate value."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Fractional CISO UK | Virtual CISO Services | Part-Time Security Leadership | 4FOX"
        description="Expert Fractional CISO services for UK businesses. Part-time cyber security leadership from £3k/month. GDPR expertise, 24/7 support, strategic guidance. Free consultation."
        canonical="https://4fox.uk/services/fractional-ciso-uk"
        localAreas={["Somerset", "North Somerset", "Weston-super-Mare", "Bristol", "South Gloucestershire"]}
        schema={{
          "@context": "https://schema.org",
          "@graph": [enhancedSchema, faqSchema]
        }}
      />
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Fractional CISO Services UK</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Strategic cyber security leadership for UK businesses. Get C-level security expertise without the full-time cost. Serving organisations across the United Kingdom with GDPR expertise and UK compliance knowledge.
            </p>

            {/* Definition Callout Box */}
            <div className="bg-accent/10 border-l-4 border-accent rounded-lg p-6 mb-8">
              <h2 className="text-xl font-bold mb-3">In a Nutshell</h2>
              <p className="text-lg">
                A <strong>Fractional CISO</strong> (also called a Virtual CISO or vCISO) provides strategic cyber security leadership to UK organisations on a <strong>part-time, contract basis</strong>. You get enterprise-level security expertise at a fraction of the cost of a full-time executive - typically <strong>£3,000-£8,000/month</strong> versus £90,000-£150,000+ annually for a full-time CISO.
              </p>
            </div>

            {/* Comparison Table */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">Fractional CISO vs Full-Time CISO vs No CISO</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-card rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-muted">
                      <th className="p-4 text-left font-semibold">Feature</th>
                      <th className="p-4 text-left font-semibold">No CISO</th>
                      <th className="p-4 text-left font-semibold bg-accent/10">Fractional CISO ✓</th>
                      <th className="p-4 text-left font-semibold">Full-Time CISO</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t">
                      <td className="p-4 font-medium">Annual Cost</td>
                      <td className="p-4">£0</td>
                      <td className="p-4 bg-accent/5"><strong>£36k-£96k/year</strong></td>
                      <td className="p-4">£90k-£150k+ (plus benefits)</td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-4 font-medium">Strategic Leadership</td>
                      <td className="p-4">❌</td>
                      <td className="p-4 bg-accent/5">✅</td>
                      <td className="p-4">✅</td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-4 font-medium">Enterprise Experience</td>
                      <td className="p-4">❌</td>
                      <td className="p-4 bg-accent/5">✅</td>
                      <td className="p-4">Varies</td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-4 font-medium">Board Reporting</td>
                      <td className="p-4">❌</td>
                      <td className="p-4 bg-accent/5">✅</td>
                      <td className="p-4">✅</td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-4 font-medium">Compliance Expertise</td>
                      <td className="p-4">❌</td>
                      <td className="p-4 bg-accent/5">✅</td>
                      <td className="p-4">✅</td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-4 font-medium">Flexibility</td>
                      <td className="p-4">N/A</td>
                      <td className="p-4 bg-accent/5"><strong>High - Scale up/down</strong></td>
                      <td className="p-4">Low - Fixed commitment</td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-4 font-medium">Day-to-Day Operations</td>
                      <td className="p-4">❌</td>
                      <td className="p-4 bg-accent/5">Strategic focus</td>
                      <td className="p-4">✅ Full coverage</td>
                    </tr>
                    <tr className="border-t">
                      <td className="p-4 font-medium">Best For</td>
                      <td className="p-4">Very small businesses</td>
                      <td className="p-4 bg-accent/5"><strong>50-500 employees</strong></td>
                      <td className="p-4">500+ employees</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Why Choose 4FOX Section */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">Why Choose 4FOX as Your Fractional CISO?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent" />
                      UK Compliance Expertise
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    Deep knowledge of GDPR, NCSC guidance, Cyber Essentials, UK NIS Regulations, and FCA requirements
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent" />
                      24/7 Availability
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    On-demand support for urgent security matters and incident response with guaranteed response times
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent" />
                      Proven Track Record
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    Successfully guided 50+ UK businesses through security transformations, achieving measurable risk reduction
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent" />
                      Industry Certifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    CISSP, CISM, Cyber Essentials certified with extensive experience across multiple industries
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Industries We Serve */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold mb-6">Industries We Serve Across the UK</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                  <Landmark className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Financial Services</h3>
                    <p className="text-sm text-muted-foreground">Banks, fintech, investment firms, insurance</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                  <Heart className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Healthcare</h3>
                    <p className="text-sm text-muted-foreground">Medical practices, clinics, health tech</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                  <Building className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Professional Services</h3>
                    <p className="text-sm text-muted-foreground">Legal, accounting, consulting firms</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                  <Code className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Technology & SaaS</h3>
                    <p className="text-sm text-muted-foreground">Software companies, tech startups, IT services</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                  <Factory className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Manufacturing</h3>
                    <p className="text-sm text-muted-foreground">Industrial, engineering, supply chain</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-card rounded-lg">
                  <ShoppingCart className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Retail & E-commerce</h3>
                    <p className="text-sm text-muted-foreground">Online stores, omnichannel retailers</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-semibold mb-4">What is a Fractional CISO?</h2>
              <p className="mb-6">
                A Fractional Chief Information Security Officer (also known as a virtual CISO, vCISO, or part-time CISO) provides strategic cyber security leadership and guidance to UK organisations without the commitment and cost of a full-time executive. Our fractional CISO services bring enterprise-level security expertise to businesses of all sizes on a part-time, contract basis.
              </p>
              <p className="mb-6">
                Based in Somerset, we serve businesses across the United Kingdom with deep knowledge of UK cyber security regulations including GDPR, NCSC guidelines, and industry-specific compliance requirements.
              </p>

              <h2 className="text-2xl font-semibold mb-4">Typical Engagement Structure</h2>
              <p className="mb-4">
                Our flexible engagement model adapts to your needs:
              </p>
              <ul className="space-y-2 mb-6">
                <li><strong>Monthly retainer</strong> includes 20-40 hours of strategic guidance and hands-on support</li>
                <li><strong>Quarterly board reports</strong> with executive-level security posture updates</li>
                <li><strong>Bi-weekly leadership meetings</strong> to review priorities and address emerging issues</li>
                <li><strong>24/7 on-demand availability</strong> for urgent security matters and incident support</li>
              </ul>

              <h2 className="text-2xl font-semibold mb-4">What You'll Receive</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Annual security roadmap</strong> aligned with business objectives and UK compliance requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Monthly risk dashboards</strong> and KPI tracking for executive visibility</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security policy development</strong> and implementation guidance for UK regulations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Incident response protocols</strong> and crisis management support (24/7 availability)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>GDPR and UK compliance guidance</strong> tailored to your industry</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security team mentoring</strong> and professional development support</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Cyber Essentials certification support</strong> and NCSC framework guidance</span>
                </li>
              </ul>

              <h2 className="text-2xl font-semibold mb-4">Our Process</h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <p className="font-semibold mb-3">First 30 Days - Assessment Phase:</p>
                <p className="mb-4">Comprehensive security posture review, stakeholder interviews, UK compliance gap analysis, and quick-win identification</p>
                
                <p className="font-semibold mb-3">Days 31-90 - Foundation Building:</p>
                <p className="mb-4">Implement critical controls, establish governance framework aligned with NCSC guidance, and deliver initial roadmap</p>
                
                <p className="font-semibold mb-3">Ongoing - Strategic Leadership:</p>
                <p>Continuous risk monitoring, strategic guidance, program evolution, and board-level reporting</p>
              </div>

              <h2 className="text-2xl font-semibold mb-4">Perfect For</h2>
              <ul className="space-y-2 mb-6">
                <li><strong>UK organisations with 50-500 employees</strong> needing executive security leadership without full-time commitment</li>
                <li><strong>Companies preparing for UK compliance audits</strong> (Cyber Essentials, ISO 27001, SOC 2, GDPR)</li>
                <li><strong>Businesses undergoing rapid growth</strong> or digital transformation requiring scalable security strategy</li>
                <li><strong>Organisations experiencing security team turnover</strong> needing interim leadership</li>
                <li><strong>UK SMEs seeking enterprise-level expertise</strong> at a fraction of full-time CISO costs</li>
              </ul>

              <h2 className="text-2xl font-semibold mb-4">Real Results from UK Businesses</h2>
              <div className="bg-accent/10 rounded-lg p-6">
                <p className="mb-3">Recent clients across the UK have achieved:</p>
                <ul className="space-y-2">
                  <li>• <strong>Cyber Essentials certification within 3 months</strong> of engagement start</li>
                  <li>• <strong>60% reduction in security incidents</strong> through improved controls and awareness training</li>
                  <li>• <strong>£150K+ in optimised security spending</strong> by rationalising tools and processes</li>
                  <li>• <strong>Board confidence improved</strong> through clear reporting and strategic communication</li>
                  <li>• <strong>GDPR compliance achieved</strong> with documented policies and controls</li>
                  <li>• <strong>Successful audit outcomes</strong> for ISO 27001 and industry-specific frameworks</li>
                </ul>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-left">
                    How much does a fractional CISO cost in the UK?
                  </AccordionTrigger>
                  <AccordionContent className="text-base">
                    Fractional CISO services typically cost <strong>£3,000-£8,000 per month</strong> depending on your organisation's size and complexity. This is significantly less than a full-time CISO salary (£90,000-£150,000+) plus benefits, offering enterprise-level expertise at a fraction of the cost. Most engagements include 20-40 hours per month of strategic guidance and support.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-left">
                    What's the difference between a fractional CISO and a vCISO?
                  </AccordionTrigger>
                  <AccordionContent className="text-base">
                    <strong>Fractional CISO and vCISO (virtual CISO) are essentially the same service</strong> with different names. Both provide part-time, strategic cyber security leadership without the full-time commitment. Some organisations also use the term "interim CISO" or "part-time CISO" to describe this service. All terms refer to experienced security executives providing strategic guidance on a contract basis.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-left">
                    Do I need a fractional CISO or a full-time CISO?
                  </AccordionTrigger>
                  <AccordionContent className="text-base">
                    <strong>UK organisations with 50-500 employees typically benefit most from a fractional CISO.</strong> If you need strategic security leadership but can't justify or afford a full-time executive, a fractional CISO provides the expertise you need at a fraction of the cost. Companies with 500+ employees, highly regulated environments, or complex security operations may require a full-time CISO for day-to-day operational oversight.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4">
                  <AccordionTrigger className="text-left">
                    How many hours per month does a fractional CISO work?
                  </AccordionTrigger>
                  <AccordionContent className="text-base">
                    Typical fractional CISO engagements include <strong>20-40 hours per month</strong> of strategic guidance and hands-on support. This covers quarterly board reports, bi-weekly leadership meetings, security roadmap development, policy reviews, and on-demand availability for urgent security matters. The exact hours can be scaled up or down based on your organisation's needs and security maturity.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger className="text-left">
                    Can a fractional CISO help with Cyber Essentials certification?
                  </AccordionTrigger>
                  <AccordionContent className="text-base">
                    <strong>Yes, absolutely.</strong> Our fractional CISO services include comprehensive guidance for achieving Cyber Essentials and Cyber Essentials Plus certification. We help you implement the required controls, prepare documentation, conduct gap assessments, and navigate the certification process. Recent clients have achieved certification within 3 months of engagement start, meeting UK government and supply chain requirements.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6">
                  <AccordionTrigger className="text-left">
                    What industries benefit most from fractional CISO services?
                  </AccordionTrigger>
                  <AccordionContent className="text-base">
                    Fractional CISO services benefit UK organisations across all industries, particularly <strong>financial services, healthcare, professional services (legal/accounting), technology companies, manufacturing, and retail</strong>. Any business handling sensitive data, facing regulatory requirements (GDPR, FCA, NIS), or requiring strategic security leadership can benefit. We have deep experience across multiple sectors and understand industry-specific compliance needs.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-7">
                  <AccordionTrigger className="text-left">
                    Is a fractional CISO available for incident response?
                  </AccordionTrigger>
                  <AccordionContent className="text-base">
                    <strong>Yes, we provide 24/7 availability for critical security incidents.</strong> Our fractional CISO services include on-demand support for urgent security matters, incident response protocols, crisis management guidance, and post-incident reviews. While we focus on strategic leadership to prevent incidents, we're available around the clock when security events occur and can coordinate your incident response efforts.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-8">
                  <AccordionTrigger className="text-left">
                    How quickly can a fractional CISO start?
                  </AccordionTrigger>
                  <AccordionContent className="text-base">
                    We can typically begin fractional CISO engagements <strong>within 1-2 weeks of initial consultation</strong>. The first 30 days focus on comprehensive security posture assessment, stakeholder interviews, UK compliance gap analysis, and quick-win identification to deliver immediate value. Our onboarding process is designed to minimise disruption while providing rapid security improvements.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            {/* CTA Section */}
            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to enhance your security leadership?</h3>
              <p className="text-muted-foreground mb-6">
                Let's discuss how our fractional CISO services can provide the strategic security guidance your UK organisation needs. Book a free consultation today to explore how we can help you achieve your security goals.
              </p>
              {consultationEnabled && (
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book Your Free Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FractionalCISOService;
